import React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Drawer as DrawerPrimitive } from 'vaul';
import { useIsDesktop } from './useIsDesktop';

type Tone = 'sage' | 'gold' | 'blush' | 'blue';

// Fonds d'illustration aux couleurs du kit v2 (indigo, lavande, or, rose).
const TONE_ART: Record<Tone, React.CSSProperties> = {
  sage: {
    background:
      'radial-gradient(ellipse 80% 100% at 30% 20%, rgba(255,151,169,0.4), transparent 70%),' +
      'radial-gradient(ellipse 60% 80% at 80% 60%, rgba(125,123,234,0.45), transparent 70%),' +
      'radial-gradient(ellipse 70% 90% at 50% 90%, rgba(75,71,196,0.25), transparent 70%),' +
      'linear-gradient(180deg, #FFFFFF, #FBF8F4)',
  },
  gold: {
    background:
      'radial-gradient(ellipse 70% 90% at 20% 30%, rgba(138,106,22,0.35), transparent 70%),' +
      'radial-gradient(ellipse 80% 100% at 90% 80%, rgba(255,151,169,0.4), transparent 70%),' +
      'linear-gradient(180deg, #FFFFFF, #FBF3DF)',
  },
  blush: {
    background:
      'radial-gradient(ellipse 80% 100% at 30% 20%, rgba(238,84,112,0.35), transparent 70%),' +
      'radial-gradient(ellipse 60% 80% at 80% 60%, rgba(255,151,169,0.45), transparent 70%),' +
      'linear-gradient(180deg, #FFFFFF, #FFE3E9)',
  },
  blue: {
    background:
      'radial-gradient(ellipse 60% 80% at 30% 30%, rgba(75,71,196,0.35), transparent 70%),' +
      'radial-gradient(ellipse 70% 90% at 80% 70%, rgba(125,123,234,0.35), transparent 70%),' +
      'linear-gradient(180deg, #FFFFFF, #EEEDFB)',
  },
};

const NOISE_SVG =
  "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix values='0 0 0 0 0.35 0 0 0 0 0.29 0 0 0 0 0.26 0 0 0 0.2 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

export interface DialogArtProps {
  tone: Tone;
  icon: React.ReactNode;
  pill?: string;
  /** Pastille en 12 px (modale de formulaire). */
  pillLarge?: boolean;
  price?: string;
  height?: number;
}

export function DialogArt({ tone, icon, pill, pillLarge = false, price, height = 120 }: DialogArtProps) {
  return (
    <div
      className="relative overflow-hidden"
      style={{ height, ...TONE_ART[tone] }}
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-50 mix-blend-multiply"
        style={{ backgroundImage: NOISE_SVG }}
        aria-hidden
      />
      {pill && (
        <span className={`absolute left-3.5 top-3.5 rounded-badge bg-white/75 px-2.5 py-1 ${pillLarge ? 'text-[12px]' : 'text-[10px]'} font-semibold uppercase tracking-[0.16em] text-[#24215C] backdrop-blur`}>
          {pill}
        </span>
      )}
      {price && (
        <span
          className="absolute right-3.5 top-3.5 rounded-badge bg-white/85 px-3 py-1.5 text-sm text-[#24215C] backdrop-blur"
          style={{ fontFamily: "'Fraunces', Georgia, serif", fontWeight: 600, letterSpacing: '-0.03em', fontVariationSettings: '"opsz" 144' }}
        >
          {price}
        </span>
      )}
      <div
        className="absolute inset-0 flex items-center justify-center text-[44px]"
        style={{ filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))' }}
        aria-hidden
      >
        {icon}
      </div>
    </div>
  );
}

export interface HybridDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  art?: React.ReactNode;
  children?: React.ReactNode;
  actions: React.ReactNode;
  /**
   * Modale de formulaire (moderation) : 480 px sur ordinateur, description en
   * 15 px, corps qui defile dans la hauteur de l'ecran et boutons fixes en bas
   * (revue du Pole design, #836).
   */
  form?: boolean;
}

/**
 * Modale hybride : Radix Dialog centrée sur desktop, vaul Drawer en bottom
 * sheet sur mobile. Même API publique pour les deux variantes.
 */
export function HybridDialog({
  open,
  onOpenChange,
  title,
  description,
  art,
  children,
  actions,
  form = false,
}: HybridDialogProps) {
  const isDesktop = useIsDesktop();
  const descId = React.useId();
  const descClass = form ? 'mb-[18px] text-[15px] leading-[1.55] text-[#6A6690]' : 'mb-[18px] text-[13.5px] leading-[1.55] text-[#6A6690]';

  if (isDesktop) {
    return (
      <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay
            className="fixed inset-0 z-[60] bg-[#17153A]/45 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          />
          <DialogPrimitive.Content
            aria-describedby={description ? descId : undefined}
            className={`fixed left-1/2 top-1/2 z-[61] w-[calc(100%-2rem)] ${form ? 'flex max-h-[calc(100dvh-24px)] max-w-[480px] flex-col' : 'max-w-[360px]'} -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-[28px] bg-white shadow-[0_24px_60px_-24px_rgba(36,33,92,0.35),0_4px_12px_-4px_rgba(36,33,92,0.12)] outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95`}
            style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
          >
            <div className={form ? 'min-h-0 flex-1 overflow-y-auto' : undefined}>
            {art}
            <div className={form ? 'px-6 pt-5' : 'px-6 pb-6 pt-5'}>
              <DialogPrimitive.Title
                className="mb-2 text-[24px] leading-[1.15] text-[#24215C]"
                style={{ fontFamily: "'Fraunces', Georgia, serif", fontWeight: 600, letterSpacing: '-0.03em', fontVariationSettings: '"opsz" 144' }}
              >
                {title}
              </DialogPrimitive.Title>
              {description && (
                <DialogPrimitive.Description
                  id={descId}
                  className={descClass}
                >
                  {description}
                </DialogPrimitive.Description>
              )}
              {children}
              {!form && <div className="flex flex-col gap-2">{actions}</div>}
            </div>
            </div>
            {form && <div className="flex shrink-0 flex-col gap-2 border-t border-[#F3F0EB] px-6 pb-6 pt-3">{actions}</div>}
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    );
  }

  return (
    <DrawerPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DrawerPrimitive.Portal>
        <DrawerPrimitive.Overlay className="fixed inset-0 z-[60] bg-[#17153A]/45" />
        <DrawerPrimitive.Content
          aria-describedby={description ? descId : undefined}
          className={`fixed inset-x-0 bottom-0 z-[61] mt-24 flex flex-col rounded-t-[28px] bg-white outline-none${form ? ' max-h-[calc(100dvh-24px)]' : ''}`}
          style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
        >
          <div
            className="absolute left-1/2 top-3 z-10 h-1 w-10 -translate-x-1/2 rounded-full"
            style={{ background: art ? 'rgba(255,255,255,0.7)' : 'rgba(36,33,92,0.2)' }}
            aria-hidden
          />
          <div className={form ? 'min-h-0 flex-1 overflow-y-auto' : undefined}>
          {art}
          <div className={form ? 'px-6 pt-5' : 'px-6 pb-6 pt-5'}>
            <DrawerPrimitive.Title
              className="mb-2 text-[22px] leading-[1.15] text-[#24215C]"
              style={{ fontFamily: "'Fraunces', Georgia, serif", fontWeight: 600, letterSpacing: '-0.03em', fontVariationSettings: '"opsz" 144' }}
            >
              {title}
            </DrawerPrimitive.Title>
            {description && (
              <DrawerPrimitive.Description
                id={descId}
                className={descClass}
              >
                {description}
              </DrawerPrimitive.Description>
            )}
            {children}
            {!form && <div className="flex flex-col gap-2">{actions}</div>}
          </div>
          </div>
          {form && <div className="flex shrink-0 flex-col gap-2 border-t border-[#F3F0EB] px-6 pb-6 pt-3">{actions}</div>}
        </DrawerPrimitive.Content>
      </DrawerPrimitive.Portal>
    </DrawerPrimitive.Root>
  );
}

type BtnVariant = 'primary' | 'cagnotte' | 'blush' | 'ghost' | 'danger';

const BTN_STYLES: Record<BtnVariant, string> = {
  primary:
    'text-white bg-[#4B47C4] hover:bg-[#3D39B0] shadow-[0_10px_24px_-12px_rgba(75,71,196,0.7)]',
  cagnotte:
    'text-white bg-[#8A6A16] hover:bg-[#735811] shadow-[0_8px_20px_-8px_rgba(138,106,22,0.6)]',
  blush:
    'text-[#B8314F] bg-[#FFE3E9]',
  ghost: 'text-[#4B47C4] bg-[#EEEDFB] hover:bg-[#E3E1F8]',
  danger:
    'text-white bg-[#B3261E] hover:bg-[#8F1E18]',
};

export interface DialogButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: BtnVariant;
}

export const DialogButton = React.forwardRef<HTMLButtonElement, DialogButtonProps>(
  function DialogButton({ variant = 'primary', className = '', ...rest }, ref) {
    return (
      <button
        ref={ref}
        type="button"
        className={`flex items-center justify-center gap-1.5 rounded-button px-4 py-3 text-[13.5px] font-semibold transition active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4B47C4] focus-visible:ring-offset-2 ${BTN_STYLES[variant]} ${className}`}
        {...rest}
      />
    );
  },
);
